import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { ApiService } from 'src/app/services';
import { Document, DocumentSerialized } from 'src/app/models/document.model';
import { DocumentType } from 'src/app/models/document_type.model';
import { DocumentRelatedType, DocumentRelatedTypeLabels } from 'src/app/enums/document-related-type.enum';
import { User } from 'src/app/models/user.model';
import { DocumentRelatedEntity } from 'src/app/models/document-related-entity.model';

@Component({
  selector: 'app-edit-document-dialog',
  templateUrl: './edit-document-dialog.component.html',
  styleUrls: ['./edit-document-dialog.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
  ],
})
export class EditDocumentDialogComponent implements OnInit {
  private fb = inject(FormBuilder);
  private api = inject(ApiService);
  private dialogRef = inject<MatDialogRef<EditDocumentDialogComponent>>(MatDialogRef);
  data = inject<EditDocumentDialogData>(MAT_DIALOG_DATA);

  form: FormGroup;
  relatedEntities: DocumentRelatedEntity[] = [];
  selectedType: DocumentType;
  DocumentRelatedType = DocumentRelatedType;
  relatedTypeLabel: string;

  constructor() {
    const data = this.data;

    this.form = this.fb.group({
      comment: [data.document.comment || '', [Validators.maxLength(255)]],
      start_date: [data.document.start_date ? new Date(data.document.start_date) : null],
      end_date: [data.document.end_date ? new Date(data.document.end_date) : null],
      related_id: [{ value: data.document.related_id || null, disabled: true }],
    });
  }

  ngOnInit(): void {
    this.selectedType = this.data.document.document_type!;
    this.relatedTypeLabel = this.getRelatedTypeLabel(this.selectedType.related_type);

    if (this.selectedType.related_type === DocumentRelatedType.User) {
      this.form.patchValue({ related_id: this.data.document.related_id });
      this.relatedEntities = [
        {
          id: this.data.document.related_id,
          name: `${this.data.user.name} ${this.data.user.surnames}`,
          trackBy: `${this.selectedType.related_type}-${this.data.document.related_id}-user`,
        },
      ];
    } else {
      this.form.patchValue({ related_id: this.data.document.related_id || null });
      this.getRelatedModels(this.selectedType.related_type, String(this.data.user.id));
    }
  }

  getRelatedModels(relatedType: string, userId: string): void {
    this.api.getRelatedModels(relatedType, Number(userId)).subscribe({
      next: (res: DocumentRelatedEntity[]): void => {
        this.relatedEntities = res.map((entity, index) => ({
          ...entity,
          trackBy: `${relatedType}-${entity.id}-${entity.start_date || ''}-${entity.end_date || ''}-${index}`,
        }));
      },
    });
  }

  save(): void {
    if (this.form.valid) {
      const formData = this.form.value;

      const data = {
        comment: formData.comment,
        start_date: formData.start_date.toISOString().slice(0, 10),
        end_date: formData.end_date ? formData.end_date.toISOString().slice(0, 10) : null,
        related_id: formData.related_id,
      };

      this.api.updateDocument(this.data.document.id, data).subscribe({
        next: (res: Document | null): void => {
          if (!res) return;

          this.dialogRef.close(res);
        },
      });
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private getRelatedTypeLabel(relatedType: string | undefined): string {
    if (!relatedType) return '';
    return DocumentRelatedTypeLabels[relatedType as DocumentRelatedType] || relatedType;
  }
}

interface EditDocumentDialogData {
  document: DocumentSerialized;
  user: User;
}
